import { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Megaphone, BadgeCheck, TrendingUp, Percent, Users as UsersIcon, Wallet, CalendarRange, MinusCircle,
} from "lucide-react";
import { PageHeader } from "@/components/EntityPage";

const LEAD_TONE = {
  "new": "bg-sky-500/20 text-sky-300 border-sky-500/40",
  "follow-up": "bg-amber-500/20 text-amber-300 border-amber-500/40",
  "confirmed": "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  "advance-received": "bg-emerald-500/25 text-emerald-200 border-emerald-500/50",
  "advance_received": "bg-emerald-500/25 text-emerald-200 border-emerald-500/50",
  "loss": "bg-red-500/20 text-red-300 border-red-500/40",
};

function StatTile({ icon: Icon, label, value, tone = "amber", testid }) {
  const tones = {
    amber: "text-amber-300 border-amber-500/40 bg-amber-500/[0.06]",
    emerald: "text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.06]",
    sky: "text-sky-300 border-sky-500/40 bg-sky-500/[0.06]",
    pink: "text-pink-300 border-pink-500/40 bg-pink-500/[0.06]",
    rose: "text-rose-300 border-rose-500/40 bg-rose-500/[0.06]",
  };
  return (
    <div data-testid={testid} className={`p-5 rounded-xl border ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
        <Icon className="w-3.5 h-3.5"/> {label}
      </div>
      <div className="mt-1 text-3xl font-display font-bold">{value}</div>
    </div>
  );
}

export default function SalesPortal() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/sales/me").then((r) => setData(r.data)).catch(() => setData({ agent: null }));
  }, []);

  if (!data) return <div className="p-8 text-center text-muted-foreground">Loading your dashboard…</div>;

  const { agent } = data;

  return (
    <div>
      <PageHeader
        title={`Hi ${agent?.name || "Sales team"}`}
        subtitle={agent ? `Employee ID: ${agent.employee_id} · Commission ${parseFloat(agent.commission_pct || 0).toFixed(2)}%` : "Your admin will link your account to a Sales Agent record shortly."}
        right={
          agent ? (
            <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40 gap-1">
              <Percent className="w-3.5 h-3.5"/> {parseFloat(agent.commission_pct || 0).toFixed(2)}%
            </Badge>
          ) : null
        }
      />

      {!agent ? (
        <Card className="p-8 bg-[hsl(224_45%_9%)] border-border text-center">
          <Megaphone className="w-10 h-10 text-amber-400 mx-auto mb-2"/>
          <div className="font-display text-lg">Your Sales profile is not linked yet.</div>
          <div className="text-sm text-muted-foreground mt-1">Please contact admin to add your agent record.</div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatTile testid="sp-leads"     icon={UsersIcon}  label="Assigned leads"    value={data.total_leads}                              tone="sky"/>
            <StatTile testid="sp-confirmed" icon={BadgeCheck} label="Confirmed leads"    value={data.confirmed_leads}                          tone="emerald"/>
            <StatTile testid="sp-trips"     icon={TrendingUp} label="Confirmed vehicles" value={data.confirmed_trips}                          tone="amber"/>
            <StatTile testid="sp-commission" icon={Wallet}    label="Commission earned"  value={fmt(data.commission_earned)}                   tone="pink"/>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatTile testid="sp-earned"   icon={TrendingUp} label="Earned (gross)"   value={fmt(data.commission_earned)} tone="pink"/>
            <StatTile testid="sp-deducted" icon={MinusCircle} label="Deducted"        value={fmt(data.commission_deducted || 0)} tone="rose"/>
            <StatTile testid="sp-paid"     icon={BadgeCheck} label="Received / paid"  value={fmt(data.commission_paid || 0)} tone="emerald"/>
            <StatTile testid="sp-pending"  icon={Wallet}     label="Pending balance"  value={fmt(data.commission_pending || 0)} tone={(data.commission_pending || 0) > 0 ? "amber" : "emerald"}/>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-5">
            <Card className="p-5 bg-[hsl(224_45%_9%)] border-border">
              <div className="flex items-center gap-2 mb-3 text-amber-400"><CalendarRange className="w-4 h-4"/><div className="font-display text-lg font-bold">Per-month performance</div></div>
              <div className="-mx-5 md:mx-0 overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead><tr className="text-[10px] uppercase text-muted-foreground bg-[hsl(224_40%_11%)]">
                    <th className="text-left p-3 sticky left-0 z-10 bg-[hsl(224_40%_11%)]">Month</th>
                    <th className="text-right p-3">Trips</th>
                    <th className="text-right p-3">Profit</th>
                    <th className="text-right p-3">Commission</th>
                  </tr></thead>
                  <tbody>
                    {data.monthly_stats.length === 0 ? <tr className="bg-[hsl(224_45%_9%)]"><td colSpan={4} className="p-6 text-center text-muted-foreground">No confirmed trips yet.</td></tr> :
                      data.monthly_stats.map((m) => (
                        <tr key={m.month} className="border-t border-border bg-[hsl(224_45%_9%)]" data-testid={`sp-month-${m.month}`}>
                          <td className="p-3 font-mono sticky left-0 z-10 bg-[hsl(224_45%_9%)]">{m.month}</td>
                          <td className="p-3 text-right font-mono">{m.trips}</td>
                          <td className="p-3 text-right font-mono text-emerald-300">{fmt(m.profit)}</td>
                          <td className="p-3 text-right font-mono text-pink-300">{fmt(m.commission)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card className="p-5 bg-[hsl(224_45%_9%)] border-border">
              <div className="flex items-center gap-2 mb-3 text-amber-400"><CalendarRange className="w-4 h-4"/><div className="font-display text-lg font-bold">Per-year performance</div></div>
              <div className="-mx-5 md:mx-0 overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead><tr className="text-[10px] uppercase text-muted-foreground bg-[hsl(224_40%_11%)]">
                    <th className="text-left p-3 sticky left-0 z-10 bg-[hsl(224_40%_11%)]">Year</th>
                    <th className="text-right p-3">Trips</th>
                    <th className="text-right p-3">Profit</th>
                    <th className="text-right p-3">Commission</th>
                  </tr></thead>
                  <tbody>
                    {data.yearly_stats.length === 0 ? <tr className="bg-[hsl(224_45%_9%)]"><td colSpan={4} className="p-6 text-center text-muted-foreground">—</td></tr> :
                      data.yearly_stats.map((y) => (
                        <tr key={y.year} className="border-t border-border bg-[hsl(224_45%_9%)]" data-testid={`sp-year-${y.year}`}>
                          <td className="p-3 font-mono sticky left-0 z-10 bg-[hsl(224_45%_9%)]">{y.year}</td>
                          <td className="p-3 text-right font-mono">{y.trips}</td>
                          <td className="p-3 text-right font-mono text-emerald-300">{fmt(y.profit)}</td>
                          <td className="p-3 text-right font-mono text-pink-300">{fmt(y.commission)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {(data.deductions || []).length > 0 && (
            <Card className="p-5 bg-[hsl(224_45%_9%)] border-border mb-5">
              <div className="flex items-center gap-2 mb-3 text-rose-300">
                <MinusCircle className="w-4 h-4"/>
                <div className="font-display text-lg font-bold">Deductions ({data.deductions.length})</div>
                <div className="ml-auto text-[10px] text-muted-foreground">Reasons visible below · contact admin for clarification</div>
              </div>
              <div className="-mx-5 md:mx-0 overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead><tr className="text-[10px] uppercase text-muted-foreground bg-[hsl(224_40%_11%)]">
                    <th className="text-left p-3 sticky left-0 z-10 bg-[hsl(224_40%_11%)]">Date</th>
                    <th className="text-left p-3">Period</th>
                    <th className="text-right p-3">Amount</th>
                    <th className="text-left p-3">Reason</th>
                    <th className="text-left p-3">Recorded by</th>
                  </tr></thead>
                  <tbody>
                    {data.deductions.map((d) => (
                      <tr key={d.id} className="border-t border-border bg-[hsl(224_45%_9%)]" data-testid={`sp-ded-${d.id}`}>
                        <td className="p-3 font-mono sticky left-0 z-10 bg-[hsl(224_45%_9%)]">{d.effective_date}</td>
                        <td className="p-3 font-mono text-amber-300">{d.period || "—"}</td>
                        <td className="p-3 text-right font-mono text-rose-300">-{fmt(d.amount)}</td>
                        <td className="p-3 text-slate-200 max-w-[320px] whitespace-pre-wrap break-words">{d.reason}</td>
                        <td className="p-3 text-muted-foreground text-xs">{d.created_by_name || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <Card className="p-5 bg-[hsl(224_45%_9%)] border-border mb-5">
            <div className="flex items-center gap-2 mb-3 text-emerald-300"><BadgeCheck className="w-4 h-4"/><div className="font-display text-lg font-bold">Payout history ({(data.payouts || []).length})</div></div>            <div className="-mx-5 md:mx-0 overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead><tr className="text-[10px] uppercase text-muted-foreground bg-[hsl(224_40%_11%)]">
                  <th className="text-left p-3 sticky left-0 z-10 bg-[hsl(224_40%_11%)]">Paid on</th>
                  <th className="text-left p-3">Period</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-left p-3">Method</th>
                  <th className="text-left p-3">Reference</th>
                  <th className="text-left p-3">Note</th>
                </tr></thead>
                <tbody>
                  {(data.payouts || []).length === 0 ? <tr className="bg-[hsl(224_45%_9%)]"><td colSpan={6} className="p-6 text-center text-muted-foreground">No payouts yet. Your admin will record them here once your commission is disbursed.</td></tr> :
                    data.payouts.map((p) => (
                      <tr key={p.id} className="border-t border-border bg-[hsl(224_45%_9%)]" data-testid={`sp-payout-${p.id}`}>
                        <td className="p-3 font-mono sticky left-0 z-10 bg-[hsl(224_45%_9%)]">{p.paid_on}</td>
                        <td className="p-3 font-mono text-amber-300">{p.period || "—"}</td>
                        <td className="p-3 text-right font-mono text-emerald-300">{fmt(p.amount)}</td>
                        <td className="p-3 uppercase text-[10px]">{p.method || "—"}</td>
                        <td className="p-3 font-mono text-xs">{p.reference || "—"}</td>
                        <td className="p-3 text-muted-foreground text-xs">{p.note || "—"}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="p-5 bg-[hsl(224_45%_9%)] border-border">
            <div className="flex items-center gap-2 mb-3 text-amber-400"><Megaphone className="w-4 h-4"/><div className="font-display text-lg font-bold">My leads</div></div>
            <div className="-mx-5 md:mx-0 overflow-x-auto">
              <table className="w-full min-w-[820px] text-sm">
                <thead><tr className="text-[10px] uppercase text-muted-foreground bg-[hsl(224_40%_11%)]">
                  <th className="text-left p-3 sticky left-0 z-10 bg-[hsl(224_40%_11%)] min-w-[160px]">Client</th>
                  <th className="text-left p-3">Mobile</th>
                  <th className="text-left p-3">Destination</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Entry</th>
                  <th className="text-right p-3">Quotation</th>
                </tr></thead>
                <tbody>
                  {(data.leads || []).length === 0 ? <tr className="bg-[hsl(224_45%_9%)]"><td colSpan={7} className="p-6 text-center text-muted-foreground">No leads assigned to you yet.</td></tr> :
                    data.leads.map((l) => (
                      <tr key={l.id} className="border-t border-border bg-[hsl(224_45%_9%)]" data-testid={`sp-lead-${l.id}`}>
                        <td className="p-3 sticky left-0 z-10 bg-[hsl(224_45%_9%)] min-w-[160px]">{l.client_name}</td>
                        <td className="p-3 font-mono text-xs">{l.mobile || "—"}</td>
                        <td className="p-3">{l.destination || "—"}</td>
                        <td className="p-3 font-mono text-amber-300 uppercase text-xs">{l.lead_type || "b2c"}</td>
                        <td className="p-3"><Badge className={LEAD_TONE[l.status] || LEAD_TONE["new"]}>{(l.status || "new").replace("_", "-")}</Badge></td>
                        <td className="p-3 font-mono text-xs">{l.entry_date || (l.created_at || "").slice(0, 10)}</td>
                        <td className="p-3 text-right font-mono text-sky-300">{l.quotation ? fmt(l.quotation) : "—"}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
